import { ref } from 'vue';
import { router as inertiaRouter, usePage } from '@inertiajs/vue3';
import { useClientStore } from '../stores/client';

/**
 * Notification composable for managing notifications
 * 
 * Provides a reactive notification queue and methods to show notifications
 * from both frontend and backend (via Pusher events)
 * 
 * Uses a singleton pattern to share state across all component instances
 * Synchronizes notifications across browser tabs using BroadcastChannel
 */
const notifications = ref([]);

// Cross-tab synchronization
let broadcastChannel = null;
let isReceivingBroadcast = false; // Flag to prevent infinite loops
let crossTabSyncInitialized = false; // Track if sync has been initialized

/**
 * Initialize cross-tab synchronization
 */
function initCrossTabSync() {
    if (crossTabSyncInitialized) {
        return; // Already initialized
    }

    // Use BroadcastChannel API if available (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
        broadcastChannel = new BroadcastChannel('notifications-sync');
        
        broadcastChannel.onmessage = (event) => {
            if (event.data.type === 'notification-added') {
                isReceivingBroadcast = true;
                const notification = event.data.notification;
                const existsById = notifications.value.some(n => n.id === notification.id);
                if (existsById) {
                    isReceivingBroadcast = false;
                    return;
                }
                if (!getRecentDuplicate({ text: notification.text, action: notification.action }, notifications.value)) {
                    notifications.value.unshift(notification);
                }
                isReceivingBroadcast = false;
            } else if (event.data.type === 'notification-removed') {
                isReceivingBroadcast = true;
                removeNotificationLocal(event.data.notificationId);
                isReceivingBroadcast = false;
            } else if (event.data.type === 'notifications-cleared') {
                isReceivingBroadcast = true;
                notifications.value = [];
                isReceivingBroadcast = false;
            }
        };
    } else {
        // Fallback to localStorage events for older browsers
        window.addEventListener('storage', handleStorageEvent);
    }

    crossTabSyncInitialized = true;
}

/**
 * Handle storage events for cross-tab sync (fallback)
 */
function handleStorageEvent(event) {
    if (event.key !== 'notifications-sync') {
        return;
    }

    try {
        const data = JSON.parse(event.newValue);
        if (!data) {
            return;
        }

        isReceivingBroadcast = true;

        if (data.type === 'notification-added') {
            const notification = data.notification;
            const existsById = notifications.value.some(n => n.id === notification.id);
            if (existsById) {
                isReceivingBroadcast = false;
                return;
            }
            if (!getRecentDuplicate({ text: notification.text, action: notification.action }, notifications.value)) {
                notifications.value.unshift(notification);
            }
        } else if (data.type === 'notification-removed') {
            removeNotificationLocal(data.notificationId);
        } else if (data.type === 'notifications-cleared') {
            notifications.value = [];
        }

        isReceivingBroadcast = false;
    } catch (e) {
        console.error('Error handling storage event:', e);
    }
}

/**
 * Broadcast notification change to other tabs
 */
function broadcastToOtherTabs(type, data) {
    if (isReceivingBroadcast) {
        return; // Don't broadcast if we're receiving a broadcast (prevent loops)
    }

    if (broadcastChannel) {
        broadcastChannel.postMessage({ type, ...data });
    } else {
        // Fallback to localStorage
        try {
            localStorage.setItem('notifications-sync', JSON.stringify({ type, ...data }));
            // Remove immediately to trigger storage event in other tabs
            setTimeout(() => {
                localStorage.removeItem('notifications-sync');
            }, 0);
        } catch (e) {
            console.error('Error broadcasting to other tabs:', e);
        }
    }
}

/**
 * Check if a candidate notification is a duplicate of one in the list (within 10s window).
 * @param {{ text: string, action?: object }} candidate - Has at least text; action optional.
 * @param {Array} existingList - Current notifications (each with text, action, timestamp).
 * @param {number} [now] - Current time (default Date.now()).
 * @returns {object|undefined} The matching existing notification, or undefined.
 */
function getRecentDuplicate(candidate, existingList, now = Date.now()) {
    return existingList.find((n) => {
        if (now - n.timestamp >= 10000) return false;
        if (n.text === candidate.text) return true;
        const a = candidate.action;
        const b = n.action;
        if (!a || !b || a.type !== b.type) return false;
        if (a.type === 'openFileFilterProposal' && a.file_id === b.file_id) return true;
        if (a.type === 'openFilterProposal' && a.data_set_id === b.data_set_id) return true;
        if (a.type === 'openSegmentProposal' && a.segment_suggestion_id === b.segment_suggestion_id) return true;
        if (a.type === 'playbookSuggestionPending') return true;
        return JSON.stringify(a) === JSON.stringify(b);
    });
}

/**
 * Remove notification locally (without broadcasting)
 */
function removeNotificationLocal(id) {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index !== -1) {
        notifications.value.splice(index, 1);
    }
}

/**
 * Cleanup cross-tab synchronization
 */
function cleanupCrossTabSync() {
    if (broadcastChannel) {
        broadcastChannel.close();
        broadcastChannel = null;
    } else {
        window.removeEventListener('storage', handleStorageEvent);
    }
}

const ROUTE_NAV_ICONS = [
    ['/playbooks', '/img/icons/nav-icons/Playbooks.svg'],
    ['/pathways', '/img/icons/nav-icons/Pathways.svg'],
    ['/insights', '/img/icons/nav-icons/Insights.svg'],
    ['/scorecard', '/img/icons/nav-icons/Scorecard.svg'],
    ['/benchmarks', '/img/icons/nav-icons/Benchmarks.svg'],
    ['/directmail', '/img/icons/nav-icons/DirectMail.svg'],
    ['/direct-mail', '/img/icons/nav-icons/DirectMail.svg'],
    ['/lockstep', '/img/icons/nav-icons/LockStep.svg'],
    ['/profile', '/img/icons/nav-icons/Profile.svg'],
    ['/settings', '/img/icons/nav-icons/Settings.svg'],
    ['/dashboard', '/img/icons/nav-icons/Dashboard.svg'],
];

/**
 * Get the appropriate nav icon based on the current route
 * @param {string} type - Notification type: 'success', 'error', 'warning', 'info', or null for default
 * @returns {string} Path to icon SVG
 */
function getNavIcon(type = null) {
    if (type === 'error' || type === 'warning') {
        return '/img/icons/avid-icons/danger.svg';
    }
    const url = (usePage()?.url || window.location.pathname) ?? '';
    const entry = ROUTE_NAV_ICONS.find(([path]) => url.includes(path));
    return entry ? entry[1] : '/img/icons/nav-icons/Dashboard.svg';
}

export function useNotifications() {
    // Initialize cross-tab sync on first use
    if (!crossTabSyncInitialized && typeof window !== 'undefined') {
        initCrossTabSync();
    }

    /**
     * Show a notification
     * @param {Object} options - Notification options
     * @param {string} options.icon - Path to icon SVG (optional, will auto-detect from route if not provided)
     * @param {string} options.text - Notification text (max 2 lines)
     * @param {string|null} options.link - Optional internal route
     * @param {string|null} options.externalLink - Optional external URL
     * @param {string|null} options.type - Notification type: 'success', 'error', 'warning', 'info' (for auto icon selection)
     * @param {boolean} options.noDismiss - If true, notification will not auto-dismiss and close button will be hidden
     * @param {Object|null} options.action - Optional action object with type and metadata
     * @returns {string} Notification ID (or null if duplicate)
     */
    function showNotification({ icon = null, text, link = null, externalLink = null, type = null, noDismiss = false, action = null }) {
        if (getRecentDuplicate({ text, action }, notifications.value)) {
            return null;
        }

        // Auto-select icon if not provided
        if (!icon) {
            icon = getNavIcon(type);
        }
        const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // Store only plain data so Vue never wraps component refs or proxies (avoids _isVue/constructor render warnings)
        let plainAction = null;
        if (action != null && typeof action === 'object') {
            try {
                plainAction = JSON.parse(JSON.stringify(action));
            } catch {
                plainAction = null;
            }
        }
        const notification = {
            id,
            icon: icon ?? null,
            text,
            link: link ?? null,
            externalLink: externalLink ?? null,
            noDismiss: Boolean(noDismiss),
            action: plainAction,
            timestamp: Date.now(),
        };

        notifications.value.unshift(notification); // Add to beginning (newest on top)

        // Broadcast to other tabs
        broadcastToOtherTabs('notification-added', { notification });

        // Auto-trigger re-enable actions when notification is added
        // Use nextTick to ensure components are ready to receive the event
        if (action && action.type === 'reEnableSuggestion') {
            // Use setTimeout to ensure event listeners are set up
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('reEnableSuggestion', {
                    detail: action,
                    bubbles: true
                }));
            }, 100);
        }

        // Auto-dismiss after 30 seconds (unless noDismiss is true)
        if (!noDismiss) {
            setTimeout(() => {
                removeNotification(id);
            }, 30000); // 30 seconds
        }

        return id;
    }

    /**
     * Show or update a progress notification
     * @param {Object} options - Progress notification options
     * @param {string} options.notificationId - Unique ID for in-place updates
     * @param {string} options.icon - Path to icon SVG
     * @param {string} options.text - Notification text
     * @param {number} options.percentage - Progress percentage (0-1)
     * @param {string} options.status - Status: 'working', 'completed', 'error'
     * @param {string|null} options.link - Optional internal route
     * @param {string|null} options.externalLink - Optional external URL
     * @returns {string} Notification ID
     */
    function showProgressNotification({ notificationId, icon, text, percentage, status, link = null, externalLink = null }) {
        // Check if notification with this ID already exists
        const existingIndex = notifications.value.findIndex(n => n.id === notificationId);
        
        const notification = {
            id: notificationId,
            icon,
            text,
            link,
            externalLink,
            percentage,
            status,
            timestamp: Date.now(),
        };

        if (existingIndex !== -1) {
            // Update existing notification
            notifications.value[existingIndex] = notification;
        } else {
            // Add new notification
            notifications.value.unshift(notification);
        }

        // Broadcast to other tabs (both new and updates)
        broadcastToOtherTabs('notification-added', { notification });

        // Progress notifications should not auto-dismiss - user must close them manually

        return notificationId;
    }

    /**
     * Remove a notification by ID
     * @param {string} id - Notification ID
     */
    function removeNotification(id) {
        const index = notifications.value.findIndex(n => n.id === id);
        if (index !== -1) {
            notifications.value.splice(index, 1);
            // Broadcast to other tabs
            broadcastToOtherTabs('notification-removed', { notificationId: id });
        }
    }

    /**
     * Clear all notifications
     */
    function clearAll() {
        notifications.value = [];
        // Broadcast to other tabs
        broadcastToOtherTabs('notifications-cleared', {});
    }

    /** Action types that dispatch a custom event (and optionally set a global); event name matches type. */
    const NOTIFICATION_ACTION_HANDLERS = {
        openColumnMappingProposal: { globalKey: '__pendingColumnMappingProposal' },
        openFileMappingProposal: { globalKey: '__pendingFileMappingProposal' },
        openFilterProposal: {},
        openFileFilterProposal: {},
        openSegmentProposal: {},
        openReportPageFilterProposal: {},
        reEnableSuggestion: {},
    };

    /**
     * Handle notification click
     * @param {Object} notification - Notification object
     */
    function handleNotificationClick(notification) {
        const actionType = notification.action?.type;
        const config = actionType && NOTIFICATION_ACTION_HANDLERS[actionType];
        if (config) {
            if (config.globalKey) {
                window[config.globalKey] = notification.action;
            }
            window.dispatchEvent(new CustomEvent(actionType, {
                detail: notification.action,
                bubbles: true,
            }));
            removeNotification(notification.id);
            return;
        }

        if (notification.externalLink) {
            window.open(notification.externalLink, '_blank');
        } else if (notification.link) {
            inertiaRouter.visit(notification.link);
        }
        if (notification.externalLink || notification.link) {
            removeNotification(notification.id);
        }
    }

    return {
        notifications,
        showNotification,
        showProgressNotification,
        removeNotification,
        clearAll,
        handleNotificationClick,
    };
}

